import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { MenuItem } from 'components/navigation/KebabMenu';
import { useCallback } from 'react';
import { useReopenMatterMutation } from 'store/api/matters';
import { MatterDto, MatterStateType } from 'types/matters';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';

/** The part of the Case detail the reopen action reads. */
export type ReopenableMatter = Pick<
    MatterDto,
    'matterStateTypeCode' | 'connectedCase' | 'allowedStatusTransitions' | 'isEditable'
>;

const REOPEN_SOURCE_STATES: string[] = [MatterStateType.OFFER_WON, MatterStateType.OFFER_LOST];

function isBlockedByConnectedCase(matter: ReopenableMatter): boolean {
    return !!matter.connectedCase;
}

function doNothing(): void {
    // A blocked reopen offers no dialog — the connected Case has to be unlinked or deleted first.
}

/**
 * The kebab item for reopening an offer outcome Case, or null when the Case cannot be reopened at all.
 * `allowedStatusTransitions` is the server's answer on whether this user may move this Case to Offer
 * Pending, so it doubles as the permission gate for the action.
 */
export function buildReopenMenuItem(matter: ReopenableMatter, openConfirmation: () => void): MenuItem | null {
    if (!REOPEN_SOURCE_STATES.includes(matter.matterStateTypeCode)) {
        return null;
    }
    // allowedStatusTransitions is privilege-only, so the per-case edit right has to be checked separately or the
    // action is offered to users whose save always fails.
    if (!matter.isEditable || !matter.allowedStatusTransitions?.includes(MatterStateType.OFFER_PENDING)) {
        return null;
    }
    const title = t('matters.reopen-as-offer-pending');
    if (isBlockedByConnectedCase(matter)) {
        return {
            title,
            subtitle: t('matters.reopen-blocked-by-connected-case'),
            disabled: true,
            handler: doNothing,
        };
    }
    return {
        title,
        handler: openConfirmation,
    };
}

function ClearedFieldsWarning(): JSX.Element {
    return (
        <div>
            <p className="text-body1">{t('matters.reopen-clears-fields-body')}</p>
            <ul className="mt-2 mb-0 list-disc pl-6">
                <li>{t('matters.reason-offer-lost')}</li>
                <li>{t('matters.outcome-notes')}</li>
                <li>{t('matters.closing-date')}</li>
            </ul>
        </div>
    );
}

export function useReopenAsOfferPending(matter: MatterDto): MenuItem | null {
    const { openModal, closeModal } = useModal();
    const [reopenMatter] = useReopenMatterMutation();
    const matterId = matter.id;
    const isOfferLost = matter.matterStateTypeCode === MatterStateType.OFFER_LOST;

    const openConfirmation = useCallback(() => {
        openModal(
            <ConfirmationModal
                titleId="matters.reopen-confirm-title"
                confirmId={isOfferLost ? 'matters.reopen-clear-and-confirm' : 'matters.reopen-confirm'}
                onCancel={closeModal}
                onConfirm={() => {
                    void reopenMatter({ id: matterId })
                        .unwrap()
                        .then(closeModal)
                        .catch((e) => {
                            logError(e, { endpoint: 'reopenMatter', matterId });
                            errorAlert(t('forms.error-submitting-the-form'));
                        });
                }}
                content={
                    isOfferLost ? (
                        <ClearedFieldsWarning />
                    ) : (
                        <p className="text-body1">{t('matters.reopen-confirm-body')}</p>
                    )
                }
            />,
            closeModal,
            { width: 480 }
        );
    }, [openModal, closeModal, reopenMatter, matterId, isOfferLost]);

    return buildReopenMenuItem(matter, openConfirmation);
}
